import { useState, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  createMerchantWithdrawal,
  getProfile,
} from "@/shared/api/services/merchantService";
import { motion, AnimatePresence } from "framer-motion";
import { Wallet, Loader2, Shield } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { PAYMENT_METHODS } from "@/shared/constants/paymentMethods";
import MethodAmountSection from "@/shared/components/merchant/withdrawals/StepMethodAmount";
import RecipientInfoSection from "@/shared/components/merchant/withdrawals/StepRecipientInfo";
import AddressSection from "@/shared/components/merchant/withdrawals/StepAddress";
const INITIAL_FORM = {
  platformName: "",
  amount: "",
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  document_id: "",
  city: "",
  address: "",
  state: "",
  postcode: "",
};
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^03\d{9}$/;
const cnicRegex = /^\d{13}$/;
const FIELD_VALIDATORS = {
  platformName: (form) => (form.platformName ? "" : "Select a payment method"),
  amount: (form, symbol) => {
    const min =
      PAYMENT_METHODS.find((m) => m.id === form.platformName)?.minAmount ?? 100;
    const val = Number(form.amount);
    return !form.amount || isNaN(val) || val < min
      ? `Minimum withdrawal is ${symbol} ${min}`
      : "";
  },
  firstname: (form) =>
    !form.firstname || form.firstname.length < 2 ? "Min 2 characters" : "",
  lastname: (form) =>
    !form.lastname || form.lastname.length < 2 ? "Min 2 characters" : "",
  email: (form) =>
    !form.email || !emailRegex.test(form.email) ? "Valid email required" : "",
  phone: (form) =>
    !form.phone || !phoneRegex.test(form.phone) ? "Format: 03XXXXXXXXX" : "",
  document_id: (form) =>
    !form.document_id || !cnicRegex.test(form.document_id)
      ? "Must be 13 digits (CNIC)"
      : "",
  city: (form) => (!form.city || form.city.length < 2 ? "Required" : ""),
  state: (form) => (!form.state || form.state.length < 2 ? "Required" : ""),
  address: (form) =>
    !form.address || form.address.length < 5 ? "Min 5 characters" : "",
  postcode: (form) =>
    !form.postcode || form.postcode.length < 3 ? "Required" : "",
};
const VALIDATED_FIELDS = [
  "platformName",
  "amount",
  "firstname",
  "lastname",
  "email",
  "phone",
  "document_id",
  "city",
  "address",
  "state",
  "postcode",
];
export default function MerchantWithdrawals() {
  const { t } = useLanguage();
  const { data: profile } = useQuery({
    queryKey: ["merchant-profile"],
    queryFn: () => getProfile(),
  });
  const currency = String(profile?.currency || "PKR").toUpperCase();
  const currencySymbol = currency === "PKR" ? "Rs" : "฿";
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const isSubmittingRef = useRef(false);
  const fieldRefs = useRef({});
  const withdrawalMutation = useMutation({
    mutationFn: (dto) => createMerchantWithdrawal(dto),
    onSuccess: () => {
      toast.success("Withdrawal request submitted successfully");
      setForm(INITIAL_FORM);
      isSubmittingRef.current = false;
    },
    onError: (error) => {
      isSubmittingRef.current = false;
      toast.error(error.message || "Withdrawal request failed");
    },
  });
  const loading = withdrawalMutation.isPending;
  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  const normalizeForm = () =>
    Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, String(v).trim()]),
    );
  const validateAll = () => {
    const normalized = normalizeForm();

    const errs = VALIDATED_FIELDS.reduce((acc, field) => {
      const msg = FIELD_VALIDATORS[field]?.(normalized, currencySymbol);
      if (msg) acc[field] = msg;
      return acc;
    }, {});

    setErrors(errs);

    const firstError = Object.keys(errs)[0];
    firstError &&
      fieldRefs.current[firstError]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

    return Object.keys(errs).length === 0;
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmittingRef.current || loading) return;
    if (!validateAll()) return;
    isSubmittingRef.current = true;
    withdrawalMutation.mutate(normalizeForm());
  };
  const isDisabled = loading || isSubmittingRef.current;
  return (
    <MerchantLayout currentPage="MerchantWithdrawals">
      <div className="max-w-2xl mx-auto px-3 sm:px-4 lg:px-0 mb-10">
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-slate-900">
                {t("withdrawals") || "Withdrawal"} — JazzCash / EasyPaisa
              </h1>
              <p className="text-slate-500">
                Send funds directly to a mobile wallet
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <MethodAmountSection
                form={form}
                updateField={updateField}
                fieldRefs={fieldRefs}
                errors={errors}
                paymentMethods={PAYMENT_METHODS}
                currencySymbol={currencySymbol}
              />
              <RecipientInfoSection
                form={form}
                updateField={updateField}
                fieldRefs={fieldRefs}
                errors={errors}
              />
              <AddressSection
                form={form}
                updateField={updateField}
                fieldRefs={fieldRefs}
                errors={errors}
              />
              {/* Submit button */}
              <div className="space-y-4">
                <Button
                  type="submit"
                  disabled={isDisabled}
                  className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 gap-2 shadow-lg shadow-emerald-100 disabled:opacity-60"
                >
                  {isDisabled ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Wallet className="h-5 w-5" />
                      Withdraw Funds
                      {parseFloat(form.amount) > 0 && (
                        <span className="ml-1 font-bold">
                          — {currencySymbol}{" "}
                          {parseFloat(form.amount).toLocaleString()}
                        </span>
                      )}
                    </>
                  )}
                </Button>
                <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <Shield className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-800 leading-relaxed">
                    Please verify all wallet details before submitting.
                    Withdrawals to mobile wallets are processed immediately and
                    are irreversible once initiated.
                  </p>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-blue-200">Amount</span>
                  <span className="font-semibold text-lg">
                    {form.amount ? `${currencySymbol} ${parseFloat(form.amount).toLocaleString()}` : "—"}
                  </span>
                </div>
                <div className="border-t border-blue-500/30 pt-3 flex justify-between text-sm">
                  <span className="text-blue-200">Recipient</span>
                  <span className="font-medium text-right truncate ml-2">
                    {form.firstname || form.lastname
                      ? `${form.firstname} ${form.lastname}`.trim()
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-blue-200">Wallet</span>
                  <span className="font-mono text-xs">{form.phone || "—"}</span>
                </div>
                {form.city && (
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-200">City</span>
                    <span className="font-medium">{form.city}</span>
                  </div>
                )}
              </div>
            </form>
          </motion.div>
        </AnimatePresence>
      </div>
    </MerchantLayout>
  );
}
